import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import {
  botPersonasAPI,
  personaAiInstructionsAPI,
  personaTopicExpertisesAPI,
  personaPhraseLibrariesAPI,
  personaKbEntriesAPI,
} from '../../api/botPersonas';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getPersonas($state) {
    return $state.records;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getPersona: $state => id => {
    return $state.records.find(r => r.id === Number(id)) || {};
  },
};

export const actions = {
  fetch: async ({ commit }) => {
    commit(types.SET_BOT_PERSONAS_UI_FLAG, { isFetching: true });
    try {
      const { data } = await botPersonasAPI.list();
      commit(types.SET_BOT_PERSONAS, data);
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_BOT_PERSONAS_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, personaData) => {
    commit(types.SET_BOT_PERSONAS_UI_FLAG, { isCreating: true });
    try {
      const { data } = await botPersonasAPI.create(personaData);
      commit(types.ADD_BOT_PERSONA, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_BOT_PERSONAS_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...personaData }) => {
    commit(types.SET_BOT_PERSONAS_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await botPersonasAPI.update(id, personaData);
      commit(types.EDIT_BOT_PERSONA, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_BOT_PERSONAS_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_BOT_PERSONAS_UI_FLAG, { isDeleting: true });
    try {
      await botPersonasAPI.delete(id);
      commit(types.DELETE_BOT_PERSONA, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_BOT_PERSONAS_UI_FLAG, { isDeleting: false });
    }
  },

  // Nested resource actions — these operate directly via API without local store
  createInstruction: async (_, { personaId, ...data }) => {
    try {
      const { data: result } = await personaAiInstructionsAPI.create(
        personaId,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },

  updateInstruction: async (_, { personaId, id, ...data }) => {
    try {
      const { data: result } = await personaAiInstructionsAPI.update(
        personaId,
        id,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },

  deleteInstruction: async (_, { personaId, id }) => {
    try {
      await personaAiInstructionsAPI.delete(personaId, id);
    } catch (error) {
      throwErrorMessage(error);
    }
  },

  createExpertise: async (_, { personaId, ...data }) => {
    try {
      const { data: result } = await personaTopicExpertisesAPI.create(
        personaId,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },

  updateExpertise: async (_, { personaId, id, ...data }) => {
    try {
      const { data: result } = await personaTopicExpertisesAPI.update(
        personaId,
        id,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },

  deleteExpertise: async (_, { personaId, id }) => {
    try {
      await personaTopicExpertisesAPI.delete(personaId, id);
    } catch (error) {
      throwErrorMessage(error);
    }
  },

  createPhrase: async (_, { personaId, ...data }) => {
    try {
      const { data: result } = await personaPhraseLibrariesAPI.create(
        personaId,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },

  updatePhrase: async (_, { personaId, id, ...data }) => {
    try {
      const { data: result } = await personaPhraseLibrariesAPI.update(
        personaId,
        id,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },

  deletePhrase: async (_, { personaId, id }) => {
    try {
      await personaPhraseLibrariesAPI.delete(personaId, id);
    } catch (error) {
      throwErrorMessage(error);
    }
  },

  createKbEntry: async (_, { personaId, ...data }) => {
    try {
      const { data: result } = await personaKbEntriesAPI.create(
        personaId,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    }
  },

  deleteKbEntry: async (_, { personaId, id }) => {
    try {
      await personaKbEntriesAPI.delete(personaId, id);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
};

export const mutations = {
  [types.SET_BOT_PERSONAS_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_BOT_PERSONAS]: MutationHelpers.set,
  [types.ADD_BOT_PERSONA]: MutationHelpers.setSingleRecord,
  [types.EDIT_BOT_PERSONA]: MutationHelpers.update,
  [types.DELETE_BOT_PERSONA]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
